import { formatNumber } from '../../lib/format/money'
import { FEATURED_CATEGORY_GROUP_NAMES } from './catalogTaxonomy'
import type { CategoryGroup } from './groupCategories'
import type { CatalogCategory } from './types'

/**
 * UID-432 / ADR-0133 — one "Featured categories" card on the `/catalog`
 * browse landing. A featured item is a taxonomy GROUP (ADR-0133 D2), not a
 * leaf category, but it navigates to a single leaf (D6) since no
 * group-level listing surface exists.
 */
export interface FeaturedCategoryCard {
  readonly groupName: string
  /** `null` when every present leaf's `seriesCount` is `null` — never a
   * fabricated "0 series" (ADR-0133 D5.3). A partial sum over the leaves
   * that DO have a count is rendered as real, just incomplete. */
  readonly seriesCountLabel: string | null
  /** The first present leaf's `imageUrl` that is non-null, or `null` if
   * none of the group's present leaves carry one. No frontend fallback
   * image map (ADR-0133 D5.4) — `CategoryTileImage` degrades to its tinted
   * glyph on `null`. */
  readonly imageUrl: string | null
  /** Up to 3 of the group's present leaf category names, in taxonomy order.
   * Never padded when fewer than 3 leaves are present. */
  readonly tags: readonly string[]
  /**
   * The leaf category a click on this card navigates to (ADR-0133 D6): the
   * group's FIRST present leaf. This is an explicit engineering call (the
   * architect flagged the multi-leaf-group destination as open) — a
   * featured card is a deep link into the group's first leaf, not a view of
   * the whole group, since no group-level listing route exists. Mirrors the
   * design source's own `onClick: () => this.openCatSub(g.subIds[0])`.
   */
  readonly destinationCategory: string
}

const MAX_TAGS = 3

/**
 * Derives the "Featured categories" cards from the SAME taxonomy-grouped
 * state (`groups`, from `useCategoryTaxonomy`'s `groupCategories()`) and
 * enriched `categoryDetails` map that `ShopByCategory` already consumes —
 * zero new fetches, zero new endpoints (ADR-0133 D4).
 *
 * Every value is real, server-derived data; any featured name that cannot
 * be backed by real data is OMITTED, never fabricated or padded
 * (ADR-0133 D5):
 *  1. a featured name matching no present taxonomy group -> card omitted;
 *  2. (implied by (1), since `groups` only contains groups with >=1 present
 *     leaf) a featured group with zero present leaves -> card omitted;
 *  3. all present leaves have `seriesCount === null` -> count line omitted,
 *     never "0 series"; a partial sum over leaves that DO have a count is
 *     rendered;
 *  4. no present leaf carries an `imageUrl` -> `imageUrl: null`, no
 *     frontend curated-fallback image map;
 *  5/6. fewer than four (or zero) cards survive -> the caller's grid simply
 *     renders fewer cells; this function does not backfill.
 */
export function deriveFeaturedCategoryCards(
  groups: readonly CategoryGroup[],
  categoryDetails: ReadonlyMap<string, CatalogCategory>,
  featuredNames: readonly string[] = FEATURED_CATEGORY_GROUP_NAMES,
): readonly FeaturedCategoryCard[] {
  const groupsByName = new Map(groups.map((group) => [group.name, group]))
  const cards: FeaturedCategoryCard[] = []

  for (const featuredName of featuredNames) {
    const group = groupsByName.get(featuredName)
    if (group === undefined || group.categories.length === 0) continue

    let sum = 0
    let hasAnyRealCount = false
    let imageUrl: string | null = null

    for (const category of group.categories) {
      const details = categoryDetails.get(category)
      if (details?.seriesCount != null) {
        sum += details.seriesCount
        hasAnyRealCount = true
      }
      if (imageUrl === null && details?.imageUrl != null) {
        imageUrl = details.imageUrl
      }
    }

    cards.push({
      groupName: group.name,
      seriesCountLabel: hasAnyRealCount ? `${formatNumber(sum)} series` : null,
      imageUrl,
      tags: group.categories.slice(0, MAX_TAGS),
      destinationCategory: group.categories[0] as string,
    })
  }

  return cards
}
